import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Layout from "layout";
import { useSelector } from "react-redux";
import React, { lazy, Suspense, useEffect } from "react";

/* pages router without lazy Load */
import Page404 from "utils/Page404";
import Login from "containers/logIn";
import Dashboard from "containers/dashboard";
import Active from "containers/servicesCall/Active";
import Reports from "containers/reports";
import ServicesCallReport from "containers/reports/report/servicesCallReport";
import MeterRead from "containers/meterRead";
import ControlAndMonitoring from "containers/controlAndMonitoring";
import MeterReadReport from "containers/reports/report/meterReadReport";
import LazyLoadLoading from "components/LazyLoadLoading";
import McAvgTempReport from "containers/reports/report/mcAvgTempReport";
import YearlyServiceCallReports from "containers/reports/report/yearlyServiceCallReports";
import CheckList from "containers/checkList/list";
import ClientsCheckList from "containers/checkList";
import CheckItem from "containers/checkList/items";
import { PLUGIN_NAME } from "./const";
import McTempReport from "containers/reports/report/mcTempReport";
import McRelayPowerReport from "containers/reports/report/mcRelayPowerReport";
import McAvgDHTReport from "containers/reports/report/mcAvgDHTReport";

const Routers = ({ setPrimaryColor, setSecondaryColor, setPaletteMode }) => {
  const isLogin = useSelector((state) => state?.userSettings?.isLogin);
  const selectedPlugin = useSelector((state) => state?.userSettings?.selectedPlugin);
  const userPaletteMode = useSelector((state) => state?.userSettings?.paletteMode);

  useEffect(() => {
    const isDark = userPaletteMode === "dark";

    if (selectedPlugin === PLUGIN_NAME.MICRO_CONTROLLERS) {
      setPrimaryColor({
        main: isDark ? "#4FB7FF" : "#0091FF",
        text: isDark ? "#f2f8fcff" : "#3A4045",
      });
      setSecondaryColor({
        main: isDark ? "#1E1E1E" : "#3A4045",
        text: isDark ? "#f2f8fcff" : "#3A4045",
      });
    }

    else if (selectedPlugin === PLUGIN_NAME.SERVICES_CALL) {
      setPrimaryColor({
        main: isDark ? "#C56DD7" : "#7B287D",
        text: isDark ? "#fceffcff" : "#423B42",
      });
      setSecondaryColor({
        main: isDark ? "#1E1E1E" : "#423B42",
        text: isDark ? "#fceffcff" : "#423B42",
      });
    }

    else if (selectedPlugin === PLUGIN_NAME.METER_READ) {
      setPrimaryColor({
        main: isDark ? "#FFC75F" : "#FFAD05",
        text: isDark ? "#fcf7eeff" : "#403E3A",
      });
      setSecondaryColor({
        main: isDark ? "#1E1E1E" : "#403E3A",
        text: isDark ? "#fcf7eeff" : "#403E3A",
      });
    }

    else {
      setPrimaryColor({
        main: isDark ? "#19D1A2" : "#13BE90",
        text: isDark ? "#effaf6ff" : "#033841",
      });
      setSecondaryColor({
        main: isDark ? "#1E1E1E" : "#033841",
        text: isDark ? "#effaf6ff" : "#033841",
      });
    }

  }, [selectedPlugin, userPaletteMode]);

  useEffect(() => {
    setPaletteMode(userPaletteMode)
  }, [userPaletteMode]);

  const lazyLoad = (importFunc) => (
    <Suspense fallback={<LazyLoadLoading />}>
      {React.createElement(lazy(importFunc))}
    </Suspense>
  );

  return (
    <Router>
      <Layout>
        <Routes>
          {isLogin
            ? <>
              <Route path="*" element={<Page404 />} />
              <Route path="/Error-Page" element={<Page404 />} />
              <Route index element={<Dashboard />} />
              <Route path="/" element={<Dashboard />} />
              <Route path="/Dashboard" element={<Dashboard />} />
              <Route path="/Biz-Settings" element={lazyLoad(() => import("containers/business"))} />
              <Route path="/Employees" element={lazyLoad(() => import("containers/employees"))} />
              <Route path="/Suppliers" element={lazyLoad(() => import("containers/suppliers"))} />
              <Route path="/Supplier-Orders" element={lazyLoad(() => import("containers/supplierOrders"))} />
              <Route path="/Items" element={lazyLoad(() => import("containers/items"))} />
              <Route path="/Departments" element={lazyLoad(() => import("containers/departments"))} />
              <Route path="/Clients" element={lazyLoad(() => import("containers/clients"))} />
              <Route path="/Calendar" element={lazyLoad(() => import("containers/calendar"))} />
              <Route path="/Meter-Read" element={<MeterRead />} />
              <Route path="/Meters" element={lazyLoad(() => import("containers/meter"))} />
              <Route path="/Schedule-ServicesCall" element={lazyLoad(() => import("containers/scheduleServices"))} />
              <Route path="/Service-Call/Active" element={<Active />} />
              <Route path="/Service-Call/Active/:callId" element={<Active />} />
              <Route path="/Service-Call/Approval" element={lazyLoad(() => import("containers/servicesCall/Approval"))} />
              <Route path="/Service-Call/History" element={lazyLoad(() => import("containers/servicesCall/History"))} />
              <Route path="/Plans" element={lazyLoad(() => import("containers/plans"))} />
              <Route path="/Plugins" element={lazyLoad(() => import("containers/plugins"))} />
              <Route path="/Micro-Controllers" element={lazyLoad(() => import("containers/microControllers"))} />
              <Route path="/Controller-Schedules" element={lazyLoad(() => import("containers/controllerSchedules"))} />
              <Route path="/Micro-Controllers/:mcGuid" element={lazyLoad(() => import("containers/microControllers"))} />
              <Route path="/Areas" element={lazyLoad(() => import("containers/areas"))} />
              <Route path="/Mini-Site" element={lazyLoad(() => import("containers/miniSite"))} />

              <Route path="/Notification" element={lazyLoad(() => import("containers/notification"))} />

              <Route path="/Control-And-Monitoring/:type/:value" element={<ControlAndMonitoring />} />
              <Route path="/Control-And-Monitoring" element={<ControlAndMonitoring />} />

              <Route path="/Check-List" element={<ClientsCheckList />} />
              <Route path="/Check-List/:clientId" element={<CheckList />} />
              <Route path="/Check-List/:clientId/:checkListId" element={<CheckItem />} />

              <Route path="/Logs" element={lazyLoad(() => import("containers/admin/logs"))} />

              {/** System */}
              <Route path="/Plugins-Manager" element={lazyLoad(() => import("containers/admin/plugins"))} />
              <Route path="/Plans-Manager" element={lazyLoad(() => import("containers/admin/plans"))} />
              <Route path="/Select-Business" element={lazyLoad(() => import("containers/admin/selectBusiness"))} />
              <Route path="/Micro-Controllers-Manager" element={lazyLoad(() => import("containers/system/mcManager"))} />
              <Route path="/Micro-Controllers-Manager/:mcGuid" element={lazyLoad(() => import("containers/system/mcManager"))} />
              <Route path="/Scan-Site" element={lazyLoad(() => import("containers/system/scanSite"))} />


              {/* Reports */}
              <Route path="/Reports" element={<Reports />} />
              <Route path="/Reports/Services-Call" element={<ServicesCallReport />} />
              <Route path="/Reports/Meter-Read" element={<MeterReadReport />} />
              <Route path="/Reports/Mc-Temp" element={<McTempReport />} />
              <Route path="/Reports/Mc-Relay-Power" element={<McRelayPowerReport />} />
              <Route path="/Reports/Mc-Avg-Temp" element={<McAvgTempReport />} />
              <Route path="/Reports/Mc-Avg-DHT" element={<McAvgDHTReport />} />
              <Route path="/Reports/Yearly-Service-Call" element={<YearlyServiceCallReports />} />

              <Route path="/Login" element={<Login />} />
              <Route path="/LoginEmp/:userName" element={<Login />} />
              <Route path="/Signup" element={lazyLoad(() => import("containers/Signup"))} />
            </>
            : <>
              <Route index element={<Login />} />
              <Route path="/Signup" element={lazyLoad(() => import("containers/Signup"))} />
              <Route path="/Login" element={<Login />} />
              <Route path="/LoginEmp/:userName" element={<Login />} />
            </>
          }
        </Routes>
      </Layout>
    </Router>
  );
};

export default Routers;
